import { logWithRethrow } from '../db/helpers';
import { LogLevel } from 'shared/types/logger';
import { isAbortError } from 'shared/utils/errorHandling';
import { isObject } from 'shared/utils/object';
import { redactUrlQueryForLog } from 'shared/utils/signedUrlFileUtils';
import { HttpError } from './httpError';
import { swLog } from './makeSwLogger';
import { buildWorkerRequestHeaders } from './requestHeaders';
import {
  NormalizedEntityResponseData,
  NormalizedPagedEntityResponseData,
  HeadersData,
} from 'serviceWorker/services/factories/types';

type ResponseToNormalize<T = unknown> = NormalizedEntityResponseData<T> & Record<string, any[]>;

type PagedResponseToNormalize<T = unknown> =
  NormalizedPagedEntityResponseData<T> & Record<string, any[]>;

/** A `Response` serializes to `{}` in a log: what tells which one it was is its status, address and type. */
function describeResponse({ status, url, type }: Response): Record<string, unknown> {
  return { status, url: redactUrlQueryForLog(url), type };
}

export const parseResponse = <T = unknown>(
  response: Response
): Promise<T> => {
  if (response.ok) {
    return response.json().catch((e) => {
      // The request was cancelled while its body was read: nothing is wrong with the response.
      if (isAbortError(e)) {
        throw e;
      }
      return logWithRethrow({
        msg: 'Could not parse response',
        logLevel: LogLevel.ERROR,
        errorObj: e,
        additionalInfo: {
          response: describeResponse(response),
        },
      });
    });
  }

  return response
    .clone()
    .json()
    .catch(() => response.clone().text().catch(() => undefined))
    .then((responseBody) => {
      throw new HttpError('Something went wrong', response.status, responseBody);
    });
};

/**
 * The answer to a change the HTTP queue sent. A 2xx means the server accepted
 * the change, also without a body (204, an empty 200) or with one that cannot
 * be read: it resolves then, with what could be read, so the queue does not
 * send the change again for ever. A failure answer rejects as `parseResponse` does.
 */
export async function parseMutationResponse(response: Response): Promise<Record<string, unknown>> {
  if (!response.ok) {
    return parseResponse(response);
  }
  const body = await response.text();
  if (!body) {
    return {};
  }
  try {
    const answer: unknown = JSON.parse(body);
    // The queue spreads the answer into the result: a string, a number or `null` would scatter or vanish.
    // A list stays as before (some answers are lists).
    return isObject<Record<string, unknown>>(answer) ? answer : {};
  } catch (error) {
    swLog('Could not parse response', LogLevel.ERROR, error, { response: describeResponse(response) });
    return {};
  }
}

export function makeNormalizeResponse<T = unknown>(
  key: string
): (response: Response) => Promise<NormalizedEntityResponseData<T>> {
  return function parseWithNormalizeResponse(
    response: Response
  ): Promise<NormalizedEntityResponseData<T>> {
    return parseResponse<ResponseToNormalize<T>>(response).then(
      (responseToNormalize: ResponseToNormalize<T>) => {
        if (key) {
          responseToNormalize.items = responseToNormalize[key];
        }
        return responseToNormalize;
      }
    );
  };
}

export function makeNormalizePagedResponse<T = unknown>(
  key: string
): (response: Response) => Promise<NormalizedPagedEntityResponseData<T>> {
  return function parseWithNormalizeResponse(
    response: Response
  ): Promise<NormalizedPagedEntityResponseData<T>> {
    return parseResponse<PagedResponseToNormalize<T>>(response).then(
      (responseToNormalize: PagedResponseToNormalize<T>) => {
        if (key) {
          responseToNormalize.items = responseToNormalize[key];
        }
        return responseToNormalize;
      }
    );
  };
}

export const defaultConfig: RequestInit = {
  mode: 'cors',
  cache: 'no-cache',
  credentials: 'same-origin',
  redirect: 'follow',
  referrerPolicy: 'no-referrer',
};

/**
 * Generate headers for HTTP requests.
 * IMPORTANT: Call ensureAuthenticatedConfig() before this function to ensure token is refreshed
 * if the Service Worker was terminated and restarted. Without a token the headers carry no
 * Authorization, and `pull` does not send such a request.
 */
export const generateHeaders = (data: Pick<HeadersData, 'locale'>): Record<string, string> =>
  buildWorkerRequestHeaders(data.locale);
